import React from 'react';
import { SearchX } from 'lucide-react';
import { Button, EmptyState, ErrorState, SkeletonRegion } from '@parsina/framework';
import MobileCardSkeleton from './MobileCardSkeleton';
import { C } from '@parsina/framework/tokens';
import { STACK } from '../constants/density';
import { useTranslation } from '../i18n';

/**
 * The standard list body: four states, and the pagination model, in one place.
 *
 * Two things it exists to stop a screen getting wrong.
 *
 * **A failed load must never render as an empty state.** `EmptyState` says
 * "there is nothing here", which a query that errored does not know. Saying it
 * anyway turns an outage into a wrong fact: it tells a desk there are no
 * patients, which invites re-registering people who already exist, and tells a
 * cashier there are no unpaid invoices, which is a money statement.
 *
 * **The two empty states are different sentences.** A filtered-empty says the
 * filters found nothing and offers to clear them; a truly-empty says the
 * dataset is empty and offers the create action. Collapsing them is how the
 * current mobile patients screen tells an operator «هنوز بیماری ثبت نشده» when
 * the clinic has two hundred and a sort order is on.
 *
 * Growth is an explicit «نمایش بیشتر», never infinite scroll: position and
 * citability still matter to someone reading a number down a phone, and the
 * caption says how far through the list they are.
 */
export default function MobileList({
  loading,
  error,
  onRetry,
  items = [],
  renderItem,
  /** True when any filter, search or non-default sort is applied. */
  filtersActive = false,
  /** The filtered-empty state. `{ icon, title, description, action }` */
  emptyFiltered,
  /** The truly-empty state. `{ icon, title, description, action }` */
  empty,
  /** `{ shown, total, onMore, loading }` — omit for a complete list. */
  more,
  skeletonCount = 5,
  /**
   * `(i) => node` — the placeholder for ONE item, shaped like the card this
   * list renders (an appointment card has an avatar and an action shelf, an
   * item card has neither), so the list keeps its geometry when the data lands.
   * Defaults to a plain `MobileCardSkeleton`.
   */
  renderSkeleton,
  gap = STACK.tight,
  errorTitle,
  errorDescription,
  'data-testid': testId,
}) {
  const { t, formatNumber } = useTranslation();

  const state = loading ? 'loading' : error ? 'error' : items.length ? 'content' : 'empty';

  // The root is a `SkeletonRegion` ONLY while loading, and a plain box in the
  // other three states.
  //
  // Both halves of that matter. A `SkeletonRegion` announces and labels itself,
  // so making it the permanent root would leave a settled list still saying
  // «در حال بارگذاری». But a hand-rolled `role="status"` is no good either: the
  // kit's region is what knows whether it is nested inside a PAGE skeleton, and
  // only a real `SkeletonRegion` falls silent when it is — a phone page that
  // draws its own region around this list would otherwise announce "loading"
  // twice (DESIGN.md §"Loading & skeleton" — region root).
  const Root = state === 'loading' ? SkeletonRegion : 'div';
  const rootProps = state === 'loading' ? { label: t('common.state.loading') } : {};

  return (
    <Root
      {...rootProps}
      data-state={state}
      data-testid={testId}
      style={{ display: 'grid', gap: `${gap}px` }}>
      {state === 'loading' && Array.from({ length: skeletonCount }, (_, i) => (
        <React.Fragment key={i}>
          {renderSkeleton ? renderSkeleton(i) : <MobileCardSkeleton />}
        </React.Fragment>
      ))}

      {state === 'error' && (
        <ErrorState
          title={errorTitle || t('errors.state.unavailable')}
          description={errorDescription || error?.message}
          onRetry={onRetry}
          retryLabel={t('common.actions.retry')}
        />
      )}

      {state === 'empty' && (
        filtersActive
          ? (
            <EmptyState
              icon={emptyFiltered?.icon || <SearchX size={44} strokeWidth={1.5} aria-hidden />}
              title={emptyFiltered?.title || t('operations.empty.noneMatchFilters')}
              description={emptyFiltered?.description || t('common.empty.clearFiltersRetry')}
              action={emptyFiltered?.action}
            />
          )
          : (
            <EmptyState
              icon={empty?.icon}
              title={empty?.title}
              description={empty?.description}
              action={empty?.action}
            />
          )
      )}

      {state === 'content' && items.map(renderItem)}

      {state === 'content' && more && more.shown < more.total && (
        <div style={{ display: 'grid', gap: '0.25rem', justifyItems: 'center', marginTop: '0.25rem' }}>
          <Button
            variant="outline"
            onClick={more.onMore}
            disabled={more.loading}
            style={{ width: '100%', minHeight: '2.75rem' }}
            data-testid={testId ? `${testId}-more` : undefined}
          >
            {more.loading ? t('common.state.loading') : t('common.actions.showMore')}
          </Button>
          <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>
            {t('common.pagination.shownOfTotal', {
              shown: formatNumber(more.shown),
              total: formatNumber(more.total),
            })}
          </span>
        </div>
      )}
    </Root>
  );
}
